import React from "react";
import Link from "gatsby-link";

import glamorous from "glamorous";
import theme from "../../styles/theme";
import MantaLogo from "../../assets/manta-equipment-logo.png";
import MantaLogoLight from "../../assets/manta-equipment-logo-light.png";
import {
  HamburgerMenu as MenuIcon,
  Login as LoginIcon,
  Close as CloseIcon
} from "../FeatureIcons";

const settingsByLocation = {
  "/": {
    backgroundColor: "#b2e0e6"
  },
  "/plans": {
    backgroundColor: "#1e485f",
    logo: MantaLogoLight,
    style: "dark",
    textColor: "#fff"
  },
  "default": {
    backgroundColor: "#ecf2f3",
    linkHoverColor: "#fff",
    logo: MantaLogo,
    textColor: "inherit",
    style: "light"
  }
};

const getSettings = pathname => ({
  ...settingsByLocation.default,
  ...settingsByLocation[pathname]
});

const LogoLink = glamorous(Link)(({ glam }) => ({
  background: `url("${getSettings(glam.location.pathname).logo}") no-repeat center center`,
  backgroundSize: "contain",
  display: "block",
  width: 125,
  height: 90,
  transition: "0.5s",
  ":hover": {
    transform: "scale(1.05, 1.05)",    
  }
}));

const HeaderDiv = glamorous.div(({ glam }) => {
  const settings = getSettings(glam.location.pathname);
  return {
    background: "transparent",
    height: 100,
    fontSize: "0.9rem",
    marginBottom: -100,
    paddingTop: "0.5rem",
    position: "absolute",
    width: "100%",
    zIndex: 1,
    "& h1, a": {
      color: settings.textColor
    }
  };
});

const NavLink = glamorous(Link)(({ theme, glam = {} }) => {
  const { linkStyle = "link", active = false, pageSettings = {} } = glam;
  const { style: pageStyle } = pageSettings;

  let linkHoverColor = theme.main.colors.light;
  if (linkStyle === "button") {
    linkHoverColor = pageStyle === "light"
      ? theme.main.colors.oceanLight
      : theme.main.colors.oceanDark;
  } else {
    linkHoverColor = pageStyle === "light"
      ? theme.main.colors.light
      : theme.main.colors.oceanLighter;
  }

  return {
    alignSelf: "center",
    background: "none",
    border: linkStyle === "button"
      ? `2px solid ${theme.main.colors.light}`
      : "none",
    borderRadius: "4px",
    display: "flex",
    fontFamily: theme.main.fontFamily,
    margin: linkStyle === "button"
      ? "1rem 20px"
      : "1rem 0",
    padding: linkStyle === "button"
      ? "3px 25px"
      : "7px 20px",
    textDecoration: active ? "underline" : "none",
    textUnderlinePosition: "under",
    textShadow: "none",
    transition: "0.5s",
    [":hover,:visited:hover"] : {
      background: linkStyle === "button"
        ? theme.main.colors.light
        : "none",
      border: linkStyle === "button"
        ? `2px solid ${theme.main.colors.light}`
        : "none",
      color: linkHoverColor,
      transition: "0.25s"
    }
  };
});

const HeaderInnerDiv = glamorous.div({
  display: "flex",
  alignItems: "stretch",
  justifyContent: "space-between",
  margin: "0 auto",
  padding: "0.25rem 0",
  maxHeight: 100,
}, ({ theme }) => ({
  maxWidth: theme.main.maxWidths.default,
  [theme.main.mediaQueries.mobile]: {
    display: "none"
  }
}));

const HeaderMobileDiv = glamorous.div(({ theme }) => ({
  display: "none",
  [theme.main.mediaQueries.mobile]: {
    display: "flex",
    alignItems: "stretch",
    justifyContent: "space-between",
    margin: "0 auto",
    padding: "0.25rem 0",
    maxHeight: 100,
  }
}));

const HeaderMobileSubDiv = glamorous.div({
  display: "flex",
  alignItems: "center"
});

const headerHeight = 90;

const HeaderMobileScrollContainer = glamorous.div({
  height: headerHeight,
  maxHeight: headerHeight,
  overflow: "hidden",
  width: "100%",
  "&> div": {
    transition: "0.5s"
  }
});

const HeaderMobileScrollMenu = glamorous.div({
  display: "flex",
  justifyContent: "space-evenly",
  height: "100%"
});

const headerToggleStyle = {
  position: "absolute",
  transition: "0.5s"
};

class MobileHeader extends React.Component {
  constructor () {
    super();
    this.state = {
      menuOpen: false
    };
  }

  toggleMenu () {
    this.setState({
      menuOpen: !this.state.menuOpen
    });
  }

  render () {
    const { location } = this.props;
    const { menuOpen } = this.state;
    const pageSettings = getSettings(location.pathname);
    const iconColorStyle = {
      "> path:not([fill='none'])": {
        fill: pageSettings.style === "light"
          ? theme.main.colors.ocean
          : theme.main.colors.light
      }
    };
    return (
      <HeaderMobileDiv>
        <glamorous.A display="flex" alignItems="center" width="2rem" marginLeft="1rem" href="#" onClick={this.toggleMenu.bind(this)}>
          <MenuIcon css={{ ...headerToggleStyle, ...iconColorStyle, transform: `rotate(${menuOpen ? 90 : 0}deg)`, opacity: menuOpen ? 0 : 1 }} />
          <CloseIcon css={{ ...headerToggleStyle, ...iconColorStyle, transform: `rotate(${menuOpen ? 0 : -90}deg)`, opacity: menuOpen ? 1 : 0 }} />
        </glamorous.A>
        <HeaderMobileScrollContainer>
          <HeaderMobileScrollMenu
            css={{
              position: "relative",
              top: menuOpen ? -headerHeight : 0,
              marginBottom: menuOpen ? -headerHeight : 0
            }}
          >
            <LogoLink to="/" glam={{ location }} css={{ marginLeft: "auto" }} />
            <HeaderMobileSubDiv css={{ marginLeft: "auto" }}>
              <glamorous.A display="flex" marginRight="1rem" href="/app">
                <LoginIcon css={iconColorStyle} />
              </glamorous.A>
            </HeaderMobileSubDiv>
          </HeaderMobileScrollMenu>
          <HeaderMobileScrollMenu>
            <NavLink
              to="/features"
              glam={{
                active: location.pathname === "/features",
                pageSettings
              }}
            >
              Features
            </NavLink>
            <NavLink
              to="/plans"
              glam={{
                active: location.pathname === "/plans",
                pageSettings
              }}
            >
              Plans
            </NavLink>
            <NavLink
              to="/app"
              glam={{
                active: false,
                pageSettings
              }}
            >
              Sign In
            </NavLink>
          </HeaderMobileScrollMenu>
        </HeaderMobileScrollContainer>
      </HeaderMobileDiv>
    );
  }
}

const DesktopHeader = ({ location }) => (
  <HeaderInnerDiv>
    <glamorous.Div display="flex" alignItems="left">
      <NavLink
        to="/features"
        glam={{
          active: location.pathname === "/features",
          pageSettings: getSettings(location.pathname)
        }}
      >
        Features
      </NavLink>
      <NavLink
        to="/plans"
        glam={{
          active: location.pathname === "/plans",
          pageSettings: getSettings(location.pathname)
        }}
      >
        Plans
      </NavLink>
    </glamorous.Div>
    <LogoLink to="/" glam={{ location }} />
    <glamorous.Div display="flex" justifyContent="right">
      <NavLink to="/app" glam={{
        linkStyle: "button",
        pageSettings: getSettings(location.pathname)
      }}>
        Sign In
      </NavLink>
    </glamorous.Div>
  </HeaderInnerDiv>
);

const Header = ({ location }) => (
  <HeaderDiv glam={{ location }}>
    <DesktopHeader location={location} />
    <MobileHeader location={location} />
  </HeaderDiv>
);

export default Header;



// WEBPACK FOOTER //
// ./src/components/Header/index.js